import { Button, ButtonColor, compact } from '@leland-dev/leland-ui-library';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

import { initializeApollo } from '../../lib/apolloClient';
import mxpnl from '../../utils/analytics/mixpanel';
import { ServerTimeLogger } from '../../utils/analytics/server-log-time';
import { categorySlugToUrn } from '../../utils/category';
import { EVENTS_RELATIVE_URL } from '../../utils/constants';
import { addUTMToEventLink, isEventShindig } from '../../utils/events';
import { generateSearchPath } from '../../utils/search';
import Breadcrumbs from '../breadcrumbs/Breadcrumbs';
import { useCategoryGoalBreadcrumbs } from '../breadcrumbs/useCategoryGoalBreadcrumbs';
import ClassCard from '../classes/ClassCard';
import Page from '../page/Page';
import PageContent, {
  getStaticPageContentQuery,
  type PageContentDataProps,
} from '../page/PageContent';
import { TalkToATeamMemberButton } from '../search/TalkToATeamMemberButton';
import { EventCard } from '../subscriber/EventCard';

import { EventCategoryDropdown } from './EventCategoryDropdown';
import { EventCategoryTags } from './EventCategoryTags';
import {
  type EventCategoryTagsCategoryFragment,
  type EventCategoryTagsPopularCategoriesQuery,
  type EventCategoryTagsPopularCategoriesQueryVariables,
} from './__generated-gql-types__/EventCategoryTags.generated';
import { EventCategoryTagsPopularCategoriesDocument } from './__generated-gql-types__/EventCategoryTags.generated';
import {
  EventsPageCategoryBySlugDocument,
  type EventsPageCategoryBySlugQuery,
  type EventsPageCategoryBySlugQueryVariables,
  EventsPageCategoryEventsCountDocument,
  type EventsPageCategoryEventsCountQuery,
  type EventsPageCategoryEventsCountQueryVariables,
  type EventsPageClassFragment,
  type EventsPageEventFragment,
  EventsPageSearchClassesAndShindigsDocument,
  type EventsPageSearchClassesAndShindigsQuery,
  type EventsPageSearchClassesAndShindigsQueryVariables,
  useEventsPageSearchClassesAndShindigsLazyQuery,
  useEventsPageSearchClassesAndShindigsQuery,
} from './__generated-gql-types__/EventPage.generated';

type EventsPageEvent = EventsPageEventFragment | EventsPageClassFragment;

export interface EventsPageProps {
  events: EventsPageEvent[];
  totalEvents: number;
  pageContentProps: PageContentDataProps;
  initialEndAt: Possible<number>;
  category?: EventsPageCategoryBySlugQuery['categoryBySlug'];
  tagCategories?: EventCategoryTagsCategoryFragment[];
}

export const EVENTS_PAGE_SIZE = 25;

const EventsPage: React.FC<EventsPageProps> = ({
  pageContentProps,
  events,
  initialEndAt,
  category,
  tagCategories,
}) => {
  const upcomingEventsEl = useRef<HTMLDivElement>(null);
  const [eventsToDisplay, setEventsToDisplay] =
    useState<EventsPageEvent[]>(events);
  const [nextStartAt, setNextStartAt] =
    useState<Possible<number>>(initialEndAt);
  const [initialClientStartAt] = useState<number>(Date.now());

  const [getMoreEventsAction, { loading: isMoreEventsLoading }] =
    useEventsPageSearchClassesAndShindigsLazyQuery();

  const { data: clientInitialEventsData } =
    useEventsPageSearchClassesAndShindigsQuery({
      variables: {
        start: initialClientStartAt ?? 0,
        count: EVENTS_PAGE_SIZE,
        categoryFilter: category?.id,
      },
    });

  useEffect(() => {
    if (clientInitialEventsData) {
      setEventsToDisplay(
        clientInitialEventsData.searchClassesAndShindigs.results,
      );
      setNextStartAt(clientInitialEventsData.searchClassesAndShindigs.end);
    }
  }, [clientInitialEventsData]);

  const loadMoreEvents = useCallback(async () => {
    if (!nextStartAt) return;
    const { data } = await getMoreEventsAction({
      variables: {
        start: nextStartAt,
        count: EVENTS_PAGE_SIZE,
        categoryFilter: category?.id,
      },
    });

    if (data) {
      setEventsToDisplay([
        ...eventsToDisplay,
        ...data.searchClassesAndShindigs.results,
      ]);
      setNextStartAt(data.searchClassesAndShindigs.end);
    }
  }, [eventsToDisplay, getMoreEventsAction, nextStartAt, category]);

  const breadcrumbs = useCategoryGoalBreadcrumbs({
    category,
    fallback: [{ label: 'Events', url: EVENTS_RELATIVE_URL }],
  });

  const headerText = category?.name
    ? `Free Events for ${category.name}`
    : 'Free Events';

  return (
    <Page title={headerText}>
      <PageContent initialData={pageContentProps}>
        <div className="pb-14">
          {/* Note: Negative margin + padding to cancel out lets scroll into view work nicely even w/ fixed header */}
          <div
            className="flex flex-col px-6 pt-8 text-left sm:m-auto sm:-mt-20 sm:max-w-7xl sm:pt-28"
            ref={upcomingEventsEl}
          >
            <div className="flex items-center justify-between">
              <Breadcrumbs breadcrumbs={breadcrumbs} />
              <div className="hidden min-h-12 sm:block">
                <TalkToATeamMemberButton
                  buttonId="EventsPage"
                  buttonLabel="Talk to a team member"
                />
              </div>
            </div>
            <div className="mb-2 mt-4">
              <h1 className="text-3xl font-medium sm:text-4xl">{headerText}</h1>
            </div>
            <EventCategoryTags
              categories={tagCategories}
              hideTags={!!category}
            />
            <div className="mt-14 min-w-64 sm:w-fit">
              <EventCategoryDropdown category={category} />
            </div>

            {/* The container div remains the same for both empty and non-empty states */}
            <div className="mt-6 flex flex-col space-y-16 sm:grid sm:grid-cols-3 sm:gap-x-6 sm:gap-y-10 sm:space-y-0">
              {!eventsToDisplay.length ? (
                <div className="w-full rounded-lg bg-leland-gray-hover p-10 text-center sm:col-span-3">
                  <div className="flex flex-col items-center justify-center gap-6">
                    <div className="space-y-2">
                      <h3 className="text-2xl font-medium">
                        No events currently available for{' '}
                        {category?.name ?? 'this category'}
                      </h3>
                      <p>
                        You can still get one-on-one coaching with an expert to
                        achieve your goals.
                      </p>
                    </div>
                    <Link
                      href={generateSearchPath({
                        goal: category?.goal,
                        categorySlug: category?.slug,
                        subCategorySlug: null,
                      })}
                    >
                      <Button
                        buttonColor={ButtonColor.BLACK}
                        label="Browse coaches"
                      />
                    </Link>
                  </div>
                </div>
              ) : (
                eventsToDisplay
                  .map((event, index) => {
                    if (isEventShindig(event)) {
                      return (
                        <div key={event.shindigId} className="sm:w-full">
                          <EventCard
                            event={{
                              ...event,
                              link: addUTMToEventLink(
                                event.link,
                                'events_page',
                              ),
                            }}
                            source="events_page"
                          />
                        </div>
                      );
                    } else if (event.isFreeEvent) {
                      const session = event.upcomingSessionsV2?.[0];
                      return (
                        <div key={event.classId} className="sm:w-full">
                          <ClassCard
                            classInfo={event}
                            classSession={session}
                            onClick={() => {
                              mxpnl.track('EventsPage - click - Class', {
                                index,
                                classId: event.classId,
                                classSlug: event.slug,
                                session,
                              });
                            }}
                            trackingPrefix="EventsPage"
                          />
                        </div>
                      );
                    }

                    return null;
                  })
                  .filter(compact)
              )}
            </div>
            <div className="mx-auto mt-10">
              {nextStartAt ? (
                <Button
                  label={isMoreEventsLoading ? 'Loading more...' : 'Load more'}
                  onClick={loadMoreEvents}
                  disabled={isMoreEventsLoading}
                />
              ) : null}
            </div>
          </div>
        </div>
      </PageContent>
    </Page>
  );
};

export interface QueryType extends Record<string, string | undefined> {
  goalSlug: string;
  categorySlug: string;
}

export const getStaticPaths: GetStaticPaths<QueryType> = async () => {
  const paths: Array<{ params: QueryType }> = [];
  const apolloClient = initializeApollo();

  const { data: categoryCounts } = await apolloClient.query<
    EventsPageCategoryEventsCountQuery,
    EventsPageCategoryEventsCountQueryVariables
  >({ query: EventsPageCategoryEventsCountDocument });

  const popularCategories = categoryCounts?.categoryEventsCount.map(
    (category) => category.category,
  );
  popularCategories.forEach((category) => {
    paths.push({
      params: { goalSlug: category.goal, categorySlug: category.slug },
    });
  });
  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<
  EventsPageProps,
  QueryType
> = async ({ params }) => {
  const { goalSlug, categorySlug } = params ?? {};
  if ((categorySlug && !goalSlug) || (goalSlug && !categorySlug)) {
    return { redirect: { destination: EVENTS_RELATIVE_URL, permanent: false } };
  }

  const TimeLoggerClient = new ServerTimeLogger();

  const pageContentCategoriesHeaderData = await getStaticPageContentQuery();
  TimeLoggerClient.logTime('page content categories header');
  const apolloClient = initializeApollo();
  const { data: eventsData } = await apolloClient.query<
    EventsPageSearchClassesAndShindigsQuery,
    EventsPageSearchClassesAndShindigsQueryVariables
  >({
    query: EventsPageSearchClassesAndShindigsDocument,
    variables: {
      start: Date.now(),
      count: EVENTS_PAGE_SIZE,
      categoryFilter: categorySlug ? categorySlugToUrn(categorySlug) : null,
    },
  });
  TimeLoggerClient.logTime('eventsData');
  const { data: categoryData } = await apolloClient.query<
    EventsPageCategoryBySlugQuery,
    EventsPageCategoryBySlugQueryVariables
  >({
    query: EventsPageCategoryBySlugDocument,
    variables: { slug: categorySlug ?? '' },
    context: { skip: !categorySlug },
  });

  const { data: popularCategories } = await apolloClient.query<
    EventCategoryTagsPopularCategoriesQuery,
    EventCategoryTagsPopularCategoriesQueryVariables
  >({
    query: EventCategoryTagsPopularCategoriesDocument,
    context: { skip: categorySlug },
  });
  const tagCategories = popularCategories?.categoryEventsCount.slice(0, 5);

  TimeLoggerClient.logTime('page content events');

  if (categorySlug && !categoryData?.categoryBySlug?.id) {
    return { redirect: { destination: EVENTS_RELATIVE_URL, permanent: false } };
  }

  return {
    props: {
      events: eventsData.searchClassesAndShindigs.results,
      totalEvents: eventsData.searchClassesAndShindigs.total,
      pageContentProps: pageContentCategoriesHeaderData,
      initialEndAt: eventsData.searchClassesAndShindigs.end,
      ...(categoryData ? { category: categoryData.categoryBySlug } : undefined),
      ...(tagCategories
        ? { tagCategories: tagCategories.map((category) => category.category) }
        : undefined),
    },
    revalidate: 3600,
  };
};

export default EventsPage;
